import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import { Text, Card, useTheme, IconButton, Chip } from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';

export interface ProgressAdjustment {
  id: string;
  type:
    | 'difficulty_change'
    | 'timeline_extension'
    | 'skill_focus'
    | 'content_adaptation';
  reason: string;
  impact: number; // -1 to 1, negative means slower progress
  confidence: number;
  aiReasoning: string;
  appliedAt: string;
}

export interface AdaptiveProgressBarProps {
  currentProgress: number; // 0-1
  predictedProgress: number; // 0-1, where we expect to be
  targetProgress?: number; // 0-1, original target
  aiAdjustments: ProgressAdjustment[];
  showPrediction?: boolean;
  animated?: boolean;
  onAdjustmentInfo?: (adjustment: ProgressAdjustment) => void;
  style?: any;
  label?: string;
  sublabel?: string;
}

export function AdaptiveProgressBar({
  currentProgress,
  predictedProgress,
  targetProgress,
  aiAdjustments = [],
  showPrediction = true,
  animated = true,
  onAdjustmentInfo,
  style,
  label,
  sublabel,
}: AdaptiveProgressBarProps) {
  const theme = useTheme();
  const [showAdjustments, setShowAdjustments] = useState(false);

  // Animation values
  const currentProgressAnim = useRef(new Animated.Value(0)).current;
  const predictedProgressAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const adjustmentIndicatorAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (animated) {
      // Animate current progress
      Animated.timing(currentProgressAnim, {
        toValue: currentProgress,
        duration: 1500,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();

      // Animate predicted progress with delay
      setTimeout(() => {
        Animated.timing(predictedProgressAnim, {
          toValue: predictedProgress,
          duration: 1000,
          easing: Easing.out(Easing.quad),
          useNativeDriver: false,
        }).start();
      }, 800);

      // Show adjustment indicators if there are recent adjustments
      if (aiAdjustments.length > 0) {
        Animated.sequence([
          Animated.delay(2000),
          Animated.timing(adjustmentIndicatorAnim, {
            toValue: 1,
            duration: 500,
            easing: Easing.out(Easing.back(1.2)),
            useNativeDriver: true,
          }),
        ]).start();
      }
    } else {
      currentProgressAnim.setValue(currentProgress);
      predictedProgressAnim.setValue(predictedProgress);
      adjustmentIndicatorAnim.setValue(aiAdjustments.length > 0 ? 1 : 0);
    }
  }, [currentProgress, predictedProgress, aiAdjustments.length, animated]);

  // Pulse animation for active adjustments
  useEffect(() => {
    if (aiAdjustments.length > 0) {
      const pulseAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      );
      pulseAnimation.start();
      return () => pulseAnimation.stop();
    }
  }, [aiAdjustments.length]);

  const getProgressColor = (progress: number) => {
    if (progress >= 0.9) return '#1B5E20'; // Dark green
    if (progress >= 0.7) return '#2E7D32'; // Green
    if (progress >= 0.5) return '#388E3C'; // Light green
    if (progress >= 0.3) return '#FBC02D'; // Yellow
    return '#F57C00'; // Orange
  };

  const getPredictionVariance = () => {
    const variance = Math.abs(predictedProgress - currentProgress);
    if (variance > 0.2) return 'high';
    if (variance > 0.1) return 'medium';
    return 'low';
  };

  const getAdjustmentImpact = () => {
    const totalImpact = aiAdjustments.reduce((sum, adj) => sum + adj.impact, 0);
    if (totalImpact > 0.1) return { type: 'positive', color: '#4CAF50' };
    if (totalImpact < -0.1) return { type: 'negative', color: '#F44336' };
    return { type: 'neutral', color: '#FF9800' };
  };

  const currentColor = getProgressColor(currentProgress);
  const predictedColor = getProgressColor(predictedProgress);
  const variance = getPredictionVariance();
  const adjustmentImpact = getAdjustmentImpact();

  return (
    <Card style={[styles.container, style]} mode='outlined'>
      <Card.Content style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.labelContainer}>
            {label && (
              <Text variant='titleMedium' style={styles.label}>
                {label}
              </Text>
            )}
            {sublabel && (
              <Text variant='bodySmall' style={styles.sublabel}>
                {sublabel}
              </Text>
            )}
          </View>

          {aiAdjustments.length > 0 && (
            <Animated.View
              style={[
                styles.adjustmentIndicator,
                {
                  transform: [
                    { scale: adjustmentIndicatorAnim },
                    { scale: pulseAnim },
                  ],
                },
              ]}
            >
              <IconButton
                icon='brain'
                size={20}
                iconColor={adjustmentImpact.color}
                onPress={() => setShowAdjustments(!showAdjustments)}
                style={[
                  styles.adjustmentButton,
                  { backgroundColor: adjustmentImpact.color + '20' },
                ]}
              />
            </Animated.View>
          )}
        </View>

        {/* Progress Values */}
        <View style={styles.progressValues}>
          <View style={styles.progressValue}>
            <Text
              variant='headlineSmall'
              style={[styles.currentValue, { color: currentColor }]}
            >
              {Math.round(currentProgress * 100)}%
            </Text>
            <Text variant='bodySmall' style={styles.valueLabel}>
              Current
            </Text>
          </View>

          {showPrediction && (
            <>
              <View style={styles.arrow}>
                <Text style={styles.arrowText}>→</Text>
              </View>

              <View style={styles.progressValue}>
                <Text
                  variant='headlineSmall'
                  style={[styles.predictedValue, { color: predictedColor }]}
                >
                  {Math.round(predictedProgress * 100)}%
                </Text>
                <Text variant='bodySmall' style={styles.valueLabel}>
                  Predicted
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Progress Bar */}
        <View style={styles.progressBarContainer}>
          {/* Background track */}
          <View
            style={[
              styles.progressTrack,
              { backgroundColor: theme.colors.surfaceVariant },
            ]}
          />

          {/* Target progress (if provided) */}
          {targetProgress && targetProgress !== predictedProgress && (
            <View
              style={[
                styles.targetIndicator,
                {
                  left: `${targetProgress * 100}%`,
                  backgroundColor: theme.colors.outline,
                },
              ]}
            />
          )}

          {/* Current progress */}
          <Animated.View
            style={[
              styles.progressFill,
              {
                width: currentProgressAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
                backgroundColor: currentColor,
              },
            ]}
          />

          {/* Predicted progress overlay */}
          {showPrediction && (
            <Animated.View
              style={[
                styles.predictedProgressFill,
                {
                  width: predictedProgressAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0%', '100%'],
                  }),
                  backgroundColor: predictedColor + '40',
                },
              ]}
            />
          )}

          {/* Adjustment markers */}
          {aiAdjustments.map((adjustment, index) => (
            <Animated.View
              key={adjustment.id}
              style={[
                styles.adjustmentMarker,
                {
                  left: `${(currentProgress + adjustment.impact * 0.1) * 100}%`,
                  backgroundColor:
                    adjustment.impact > 0 ? '#4CAF50' : '#F44336',
                  opacity: adjustmentIndicatorAnim,
                },
              ]}
            />
          ))}
        </View>

        {/* Variance indicator */}
        {showPrediction && variance !== 'low' && (
          <View style={styles.varianceIndicator}>
            <Chip
              icon={variance === 'high' ? 'alert' : 'information'}
              mode='outlined'
              compact
              style={[
                styles.varianceChip,
                {
                  borderColor: variance === 'high' ? '#FF9800' : '#2196F3',
                },
              ]}
              textStyle={[
                styles.varianceText,
                { color: variance === 'high' ? '#FF9800' : '#2196F3' },
              ]}
            >
              {variance === 'high' ? 'High variance' : 'Moderate variance'}
            </Chip>
          </View>
        )}

        {/* AI Adjustments Panel */}
        {showAdjustments && aiAdjustments.length > 0 && (
          <View style={styles.adjustmentsPanel}>
            <Text variant='titleSmall' style={styles.adjustmentsTitle}>
              AI Progress Adjustments
            </Text>

            {aiAdjustments.slice(0, 3).map(adjustment => (
              <View
                key={adjustment.id}
                style={[
                  styles.adjustmentItem,
                  {
                    borderLeftColor:
                      adjustment.impact > 0 ? '#4CAF50' : '#F44336',
                  },
                ]}
              >
                <View style={styles.adjustmentHeader}>
                  <Text variant='bodyMedium' style={styles.adjustmentReason}>
                    {adjustment.reason}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.adjustmentImpact,
                      { color: adjustment.impact > 0 ? '#4CAF50' : '#F44336' },
                    ]}
                  >
                    {adjustment.impact > 0 ? '+' : ''}
                    {Math.round(adjustment.impact * 100)}%
                  </Text>
                </View>

                <Text variant='bodySmall' style={styles.adjustmentAI}>
                  {adjustment.aiReasoning}
                </Text>

                {onAdjustmentInfo && (
                  <IconButton
                    icon='information'
                    size={16}
                    onPress={() => onAdjustmentInfo(adjustment)}
                    style={styles.adjustmentInfoButton}
                  />
                )}
              </View>
            ))}

            {aiAdjustments.length > 3 && (
              <Text variant='bodySmall' style={styles.moreAdjustments}>
                +{aiAdjustments.length - 3} more adjustments
              </Text>
            )}
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  content: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  labelContainer: {
    flex: 1,
  },
  label: {
    fontWeight: '600',
    marginBottom: 4,
  },
  sublabel: {
    opacity: 0.7,
  },
  adjustmentIndicator: {
    marginLeft: 8,
  },
  adjustmentButton: {
    margin: 0,
  },
  progressValues: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  progressValue: {
    alignItems: 'center',
    flex: 1,
  },
  currentValue: {
    fontWeight: '700',
    marginBottom: 4,
  },
  predictedValue: {
    fontWeight: '600',
    marginBottom: 4,
  },
  valueLabel: {
    opacity: 0.7,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  arrow: {
    paddingHorizontal: 16,
  },
  arrowText: {
    fontSize: 20,
    opacity: 0.6,
  },
  progressBarContainer: {
    height: 12,
    position: 'relative',
    marginBottom: 12,
  },
  progressTrack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 6,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 6,
    zIndex: 2,
  },
  predictedProgressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 6,
    zIndex: 1,
  },
  targetIndicator: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 16,
    zIndex: 3,
    borderRadius: 1,
  },
  adjustmentMarker: {
    position: 'absolute',
    top: -1,
    width: 4,
    height: 14,
    borderRadius: 2,
    zIndex: 4,
  },
  varianceIndicator: {
    alignItems: 'center',
    marginTop: 8,
  },
  varianceChip: {
    backgroundColor: 'transparent',
  },
  varianceText: {
    fontSize: 12,
  },
  adjustmentsPanel: {
    marginTop: 16,
    padding: 12,
    backgroundColor: 'rgba(25, 118, 210, 0.05)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(25, 118, 210, 0.2)',
  },
  adjustmentsTitle: {
    fontWeight: '600',
    marginBottom: 12,
    color: '#1976D2',
  },
  adjustmentItem: {
    padding: 8,
    marginBottom: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: 6,
    borderLeftWidth: 3,
    position: 'relative',
  },
  adjustmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  adjustmentReason: {
    flex: 1,
    fontWeight: '500',
  },
  adjustmentImpact: {
    fontWeight: '600',
    fontSize: 12,
  },
  adjustmentAI: {
    opacity: 0.8,
    lineHeight: 16,
  },
  adjustmentInfoButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    margin: 0,
  },
  moreAdjustments: {
    textAlign: 'center',
    opacity: 0.7,
    fontStyle: 'italic',
  },
});
